import * as Maths3 from "oc-threejs/Maths3";
import { v3To2xz } from "oc-threejs/Maths3";
import * as Commons from "oc-commons/Commons";
import { v3 } from "oc-threejs/short3";
import {
  filterPathIntersection,
  filterPathIntersectionCached,
  findPathIntersection,
  findPathIntersectionCached,
  following_are_optionals,
} from "oc-app/metadata/GraphMetadata";
import { getMMR } from "../RichMetadata";
import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import _ from "lodash";
import { isElementhalterFn } from "./elementFunctions";
import { Pfosten } from "./Pfosten";
import { ZElement } from "./ZElement";
import { Arrangement } from "./Arrangement";
import { Box3, Vector3 } from "three";
import {
  MAuswahlwert,
  MElement,
  MKeyTable,
  MModelMaterialRelation,
  MObjekt,
  MPfosten,
  MPfosten_Hoehenversatz,
  MUMProfil,
} from "../../metamodell";
import { PAuswahlwert, PElementhalter, PMass, PPfosten } from "../../metamodell_paths";
import {
  AngleIntervalType,
  findMatchingRotation,
  getAngleIntervalType,
  roundAngle,
} from "../commons/AngleIntervals";
import { v2ToAngle } from "./grundrissFunctions";
import { pfostenbreiteCirca } from "./constants";
import { Ecke } from "./Ecke";
import { Segment } from "./Segment";
import { UMProfil } from "./UMProfil";
import { ModelGhost } from "oc-jaco3d/arrangement/ModelGhost";

const epsilon: number = 0.05;

export function elementHöheAmPfosten(
  pfosten: Pfosten,
  element: ZElement,
  istLinks: boolean,
  mitPfostenHöhenversatz: boolean | undefined = undefined
): number {
  return elementMetaHöheAmPfosten({
    elementMeta: element.meta,
    istLinks,
    anschlussAufRechts: element.anschlussAufRechts,
    höhe: element.size.y,
    mitPfostenHöhenversatz,
    pfosten,
  });
}

interface ElementMetaHöheAmPfostenParams {
  elementMeta: MElement;
  istLinks: boolean;
  anschlussAufRechts: boolean;
  höhe: number;
  mitPfostenHöhenversatz?: boolean;
  pfosten: Pfosten;
}

function elementMetaHöheAmPfosten({
  elementMeta,
  istLinks,
  anschlussAufRechts,
  höhe,
  mitPfostenHöhenversatz = true,
  pfosten,
}: ElementMetaHöheAmPfostenParams): number {
  const elementMass = elementMeta.Mass;
  const delta = istLinks == anschlussAufRechts ? elementMass.DeltaRechts : elementMass.DeltaLinks;
  const höhenversatz = mitPfostenHöhenversatz
    ? (
        findPathIntersection(
          elementMass,
          (m: PMass) => m.Pfosten_Hoehenversatz,
          pfosten.meta,
          (p: PPfosten) => p.Pfostenmass.Pfosten_Hoehenversatz
        ) as MPfosten_Hoehenversatz
      )?.Höhenversatz ?? 0
    : 0;

  return elementMass.PositionY + höhe + delta + höhenversatz;
}

export interface PfostenMetaParameter {
  pfosten?: Pfosten;
  pfostenfarbeAuswahlwert?: MAuswahlwert;
  pfostenartAuswahlwert?: MAuswahlwert;
  bodenbefestigungAuswahlwert?: MAuswahlwert;
  elementhalterAuswahlwert?: MAuswahlwert;
  ausgleichGefälle?: boolean;
  meta?: any; // Ersetze 'any' durch den tatsächlichen Typ
  pfostenMetas?: any[]; // Ersetze 'any' durch den tatsächlichen Typ
  elemente?: (ZElement | MElement)[];
  montageadapterAuswahlwert?: MAuswahlwert;
  verbundankerAuswahlwert?: MAuswahlwert;
  ummantelungAuswahlwert?: MAuswahlwert;
  ankerAuswahlwert?: MAuswahlwert;
  pfostenkappeAuswahlwert?: MAuswahlwert;
  angles?: PfostenWinkel[];
  gesuchtePfostenHöhe?: number;
  isMaxElementHöhe?: boolean;
  isAufgeschraubt?: boolean;
  isTor?: boolean;
  isUMProfil?: boolean;
  hasAnker?: boolean;
  mauer?: boolean;
  toraufhängung?: boolean;
  torschloss?: boolean;
  aufbautyp?: MObjekt;
  anschlagprofilAuswahlwert?: MAuswahlwert;
  anschlagprofilfarbeAuswahlwert?: MAuswahlwert;
  warning?: string | null;
  box?: Box3;
  breite?: number;
  echteLänge?: number;
  dy?: number;
}

export function sammleZusammenhängendeToreUndAngrenzendeElemente(pfosten: Pfosten): {
  tore: ZElement[];
  elemente: ZElement[];
} {
  const visited = new Set<any>();
  const elemente: ZElement[] = [];
  const tore: ZElement[] = [];
  const notVisited = (x) => x != null && !visited.has(x);
  const visit = (pfosten: Pfosten, fromTor: boolean) => {
    visited.add(pfosten);
    for (const element of pfosten.elemente.filter(notVisited)) {
      visited.add(element);
      const isTor = element.isTor;
      (isTor ? tore : elemente).push(element);
      if (fromTor && isTor) {
        for (const p of element.getPfosten().filter(notVisited)) {
          visit(p, isTor);
        }
      }
    }
    pfosten.elemente.filter(notVisited).forEach((element) => {});
  };
  visit(pfosten, true);
  return { tore, elemente };
}

export function getPfostenMetaParameterFromPfosten(
  pfosten: Pfosten,
  { mitTorpfostenSonderhöhe } = { mitTorpfostenSonderhöhe: false }
): PfostenMetaParameter {
  // PROBLEM hier: Element ist noch nicht geupdated, hat kein .meta...

  const pfostenMetaParameter = getPfostenInstanzParameter({
    ...pfosten,
    arrangement: pfosten.arrangement,
    pfosten: pfosten,
  });

  const { elemente, tore } = sammleZusammenhängendeToreUndAngrenzendeElemente(pfosten);

  let pfostenMetas: MPfosten[];
  const mapPfostenPaths = (element) => [element, (element) => element.meta.Pfostentyp.Pfosten];
  // Wenn ein Schiebetor direkt am Pfosten anliegt, müssen IMMER die speziellen
  // Schiebetorpfosten verwendet werden, unabhängig davon welche anderen Tore/Elemente
  // (z.B. ein angrenzendes Einzeltor) ebenfalls anliegen. Eine geometrisch ungültige
  // Anordnung (Einzeltor an der Doppelpfosten-/Öffnungsseite) wird separat per
  // Warnung gemeldet, darf aber den Pfostentyp am Schiebetor nicht ändern.
  const direktAdjacentSchiebetor = pfosten.elemente.find((e) => e?.isSchiebetor);
  if (direktAdjacentSchiebetor != null) {
    pfostenMetas = /*UNCACHED*/ filterPathIntersection(
      direktAdjacentSchiebetor,
      (e: any) => e.meta.Pfostentyp.Pfosten
    ) as MPfosten[];
  } else if (tore.length > 0) {
    pfostenMetas = /*UNCACHED */ filterPathIntersection(
      // @ts-ignore
      ...tore.flatMap(mapPfostenPaths),
      // @ts-ignore
      ...elemente.flatMap(mapPfostenPaths)
    );
    if (pfostenMetas.length == 0) {
      const mapTorpfostenÜberPfostenartRelation = (element) => [
        element,
        (element) =>
          element.meta.Pfostentyp.Pfosten.Pfostenart.PfostenartRelation.Torpfostenart.Pfosten,
      ];
      // FIXME diese uncached filterPathIntersection, insbesondere beim Weg über sehr große Anzahlen wie Pfostentyp->Pfosten, könnten performancekritisch sein
      // Man könnte hier evtl. ein Caching über einen Set von Pfostentypen auf Pfostenmetas machen
      pfostenMetas = /*UNCACHED*/ filterPathIntersection(
        // @ts-ignore
        ...tore.flatMap(mapPfostenPaths),
        following_are_optionals,
        // @ts-ignore
        ...elemente.flatMap(mapTorpfostenÜberPfostenartRelation)
      );
    }
  } else {
    pfostenMetas = /*UNCACHED*/ (
      filterPathIntersection(
        // @ts-ignore
        ...elemente.flatMap(mapPfostenPaths)
      ) as MPfosten[]
    ).filter((p) => p.Pfostentyp.Key != "Torpfosten");
  }

  if (tore.length > 0 && mitTorpfostenSonderhöhe) {
    const torPfosten = new Set(
      tore.flatMap((tor) => tor.getPfosten().filter((p) => p?.isTorpfosten))
    );
    // Wir berechnen die minimale Höhe, die der Pfosten OHNE Berücksichtigung
    // des pfostenspezifischen Höhenversatzes benötigt.
    // Das verhindert Abweichungen, die entstehen, weil Pfosten in vorherigen Iterationen unterschiedliche Metas hatten.
    const aktuelleGesuchtePfostenHöheOhneVersatz = (p: Pfosten) =>
      p.elemente.reduce((max, element) => {
        const istLinks =
          element.pfostenLinks === p ||
          (element.pfostenLinks == null && element.pfostenRechts !== p);
        const elementMass = element.meta.Mass;
        const delta =
          istLinks == element.anschlussAufRechts ? elementMass.DeltaRechts : elementMass.DeltaLinks;
        return Math.max(max, elementMass.PositionY + element.size.y + delta);
      }, 0);

    // Ein Tor benötigt eine Sonderanfertigung ('fertigung') zwingend, wenn die anliegenden
    // Elemente asymmetrisch stark abweichende Pfostenhöhen erfordern. Dies verhindert,
    // dass eine Seite einen Standard-Fix-Pfosten wählt und die andere Seite eine Fertigung zieht,
    // was das Torpfostenset zerschneiden würde.
    const isTorpfostenSonderhöhe =
      mitTorpfostenSonderhöhe &&
      (() => {
        const torPfostenArray = Array.from(torPfosten);
        if (torPfostenArray.some((p) => p.isHolzpfosten)) return false;

        // 1. Unterscheiden sich die benötigten Höhen der einzelnen Pfosten innerhalb
        // des Torpfastensets signifikant? (Beispiel: Zaun 1.80m -> Tor 0.90m -> Tor 0.90m -> Zaun 0.90m)
        // Falls ja: Ein Pfosten braucht ein 1.80m Set, der andere ein 0.90m Set -> Sonderanfertigung,
        // da Torpfostensets immer paarweise aus derselben Kategorie stammen müssen.
        const reqs = torPfostenArray.map((p) => aktuelleGesuchtePfostenHöheOhneVersatz(p));
        const maxReq = Math.max(...reqs);
        const minReq = Math.min(...reqs);
        if (maxReq - minReq > 0.06) return true;

        // 2. Verbindet ein einzelner Mittelpfosten zwei Tore mit signifikant unterschiedlichen
        // Höhen? (Beispiel: Dreifachtor mit 1.50m und 1.80m Tor)
        // Falls ja: Es gibt keinen Standard-Fix-Mittelpfosten mit asymmetrisch positionierten
        // Scharnieren -> Sonderanfertigung zwingend.
        return torPfostenArray.some((p) => {
          const connectedTors = p.elemente.filter((e) => e.isTor);
          if (connectedTors.length > 1) {
            const torReqs = connectedTors.map((tor) => {
              const torMass = tor.meta.Mass;
              const istLinks =
                tor.pfostenLinks === p || (tor.pfostenLinks == null && tor.pfostenRechts !== p);
              const torDelta =
                istLinks == tor.anschlussAufRechts ? torMass.DeltaRechts : torMass.DeltaLinks;
              return torMass.PositionY + tor.size.y + torDelta;
            });
            if (Math.max(...torReqs) - Math.min(...torReqs) > 0.06) return true;
          }
          return false;
        });
      })();

    if (isTorpfostenSonderhöhe) {
      pfostenMetas = pfostenMetas.filter((p) => p.Pfostenmass.Variabel == "fertigung");
    }
  }

  // Für Doppeltore mit E-Antrieb (Torpfosten müssen einbetoniert werden)
  const möglicheBodenbefestigungen = /*UNCACHED*/ filterPathIntersection(
    // @ts-ignore
    ...pfosten.elemente
      .map((e) => [e, (element) => element.meta.Bodenbefestigung.Auswahlwert])
      .flat()
  ) as MAuswahlwert[];
  const nurEinbetoniertePfosten = !pfosten.isAufschraubpfostenMöglich;
  const einbetoniertAuswahlwert = APP.richMetadata.findAuswahlwert("Einbetoniert im Boden");
  const bodenbefestigungAuswahlwert =
    (findPathIntersectionCached("pfostenFunctions.js:74")(
      möglicheBodenbefestigungen,
      (b: PAuswahlwert) => b,
      nurEinbetoniertePfosten
        ? einbetoniertAuswahlwert
        : pfosten.bodenbefestigungAuswahlwert ??
            pfosten.arrangement.bodenbefestigungAuswahlwert ??
            pfosten.arrangement.bodenbefestigungAuswahlwertDefault,
      (b: PAuswahlwert) => b
    ) as MAuswahlwert) ?? möglicheBodenbefestigungen[0];

  const möglicheElementhalter = pfosten.elemente.map((e) => e.meta.Elementhalter).flat();
  const elementhalterAuswahlwert =
    (findPathIntersectionCached("pfostenFunctions.js:85")(
      möglicheElementhalter,
      (b: PElementhalter) => b.Auswahlwert,
      pfosten.elementhalterAuswahlwert ?? pfosten.arrangement.elementhalterAuswahlwert,
      (b: PAuswahlwert) => b
    ) as MAuswahlwert) ??
    _.sortBy(möglicheElementhalter, (e) => -Number(e?.Auswahlwert?.Voreinstellung)).find(
      (e) => e !== null
    )?.Auswahlwert;

  return {
    ...pfostenMetaParameter,
    pfostenfarbeAuswahlwert:
      pfosten.pfostenfarbeAuswahlwert ?? pfosten.arrangement.pfostenfarbeAuswahlwert,
    pfostenartAuswahlwert:
      pfosten.pfostenartAuswahlwert ?? pfosten.arrangement.pfostenartAuswahlwert,
    anschlagprofilfarbeAuswahlwert: pfosten.anschlagprofilfarbeAuswahlwert,
    bodenbefestigungAuswahlwert,
    elementhalterAuswahlwert,
    ausgleichGefälle: pfosten.ausgleichGefälle,
    meta: pfosten.meta,
    pfostenMetas,
    elemente: pfosten.elemente.map((e) => e?.meta),
    montageadapterAuswahlwert: pfosten.montageadapterAuswahlwert,
    verbundankerAuswahlwert: pfosten.verbundankerAuswahlwert,
    ummantelungAuswahlwert: pfosten.ummantelungAuswahlwert,
    ankerAuswahlwert: pfosten.ankerAuswahlwert,
    pfostenkappeAuswahlwert: pfosten.pfostenkappeAuswahlwert,
  };
}

export interface PfostenWinkelInput {
  angle: number;
  flacheSeiteNotwendig: boolean;
}

export interface PfostenWinkel extends PfostenWinkelInput {
  type: AngleIntervalType;
  element: ZElement;
}

export function getPfostenWinkel(pfosten: Pfosten, element: ZElement): PfostenWinkel {
  const segment = element.segment;
  const elementCenter2d = v3To2xz(segment.getWorldPositionOfX(segment.getX(element, 0.5)));
  const pfostenCenter2d = v3To2xz(pfosten.getWorldPosition());
  const angle = roundAngle(v2ToAngle(elementCenter2d.sub(pfostenCenter2d)));
  const type = getAngleIntervalType(pfosten, angle);
  const flacheSeiteNotwendig = element.isTor;
  return {
    angle,
    flacheSeiteNotwendig,
    type,
    element,
  };
}

export function getPfostenWinkelListe(pfosten: Pfosten, elemente: ZElement[]): PfostenWinkel[] {
  return _.sortBy(
    elemente.map((element: ZElement) => getPfostenWinkel(pfosten, element)),
    ({ angle }) => angle
  );
}

export function getPfostenInstanzParameter({
  arrangement,
  elemente,
  pfosten,
  anMauer,
}: {
  arrangement: Arrangement;
  elemente: ZElement[];
  pfosten: Pfosten;
  anMauer?: boolean;
}): PfostenMetaParameter {
  if (!arrangement.isArrangement) {
    throw new Error("parameter arrangement must be of class Arrangement");
  }
  if (elemente.length == 0 || !elemente.some((e) => e.isElement)) {
    throw new Error(
      "parameter elemente must be an array of class ZElement with at least one element"
    );
  }
  if (!pfosten.isPfosten) {
    throw new Error("parameter pfosten must be of class Pfosten");
  }

  const elementeFürPfostenhöhe = elemente;
  const gesuchtePfostenHöhe = elementeFürPfostenhöhe.reduce(
    (max, element) =>
      Math.max(
        max,
        elementHöheAmPfosten(
          pfosten,
          element,
          element.pfostenLinks === pfosten ||
            (element.pfostenLinks == null && element.pfostenRechts !== pfosten)
        )
      ),
    0
  );

  // isMaxElementHöhe steuert nur die Bevorzugung von Fertigungs- vs. Fix-Pfosten (siehe
  // pfostenScore). Für Torpfosten darf diese Entscheidung NICHT von angrenzenden (ggf.
  // gekürzten) Zaunelementen abhängen: Das Torpfosten-Set richtet sich allein nach dem Tor.
  // Sonst zieht ein niedrigeres Nachbarelement einseitig eine Sonderhöhen-Fertigung und das
  // Torpfosten-Set wird zwischen Fix (eine Seite) und Fertigung (andere Seite) zerschnitten.
  const torElementeFürMaxHöhe = elementeFürPfostenhöhe.filter((e) => e.isTor);
  const elementeFürMaxHöhe =
    torElementeFürMaxHöhe.length > 0 ? torElementeFürMaxHöhe : elementeFürPfostenhöhe;
  const isMaxElementHöhe = elementeFürMaxHöhe.every(
    (e) => e.höhe + epsilon > e.sizeRange.box.max.y
  );
  const tor = elemente.find((e) => e.isTor);
  const aufhängungsTor = elemente.find((e) => e.isTor && e.hasAufhängungAnPfosten(pfosten));
  const schlossTor = elemente.find((e) => e.isTor && e.hasSchlossAnPfosten(pfosten));
  const isTor = tor != null;
  const aufbautyp = (tor ?? elemente[0])?.meta?.Aufbautyp;

  if (aufbautyp == null) debugger;

  const mauer = anMauer;

  const toraufhängung = aufhängungsTor != null;
  const torschloss = schlossTor != null;
  const anschlagprofilAuswahlwert = schlossTor?.anschlagprofilAuswahlwert;
  const angles = getPfostenWinkelListe(pfosten, elemente);

  return {
    pfosten,
    angles,
    gesuchtePfostenHöhe,
    isMaxElementHöhe,
    isTor,
    mauer,
    toraufhängung,
    torschloss,
    aufbautyp,
    anschlagprofilAuswahlwert,
  };
}

export function getPfostenMetaParameter({
  pfosten,
  pfostenfarbeAuswahlwert,
  pfostenartAuswahlwert,
  bodenbefestigungAuswahlwert,
  elementhalterAuswahlwert,
  ausgleichGefälle,
  meta,
  pfostenMetas,
  elemente,
  montageadapterAuswahlwert,
  verbundankerAuswahlwert,
  ummantelungAuswahlwert,
  ankerAuswahlwert,
  pfostenkappeAuswahlwert,

  // werden von getPfostenInstanzParameter erzeugt
  angles,
  gesuchtePfostenHöhe,
  isMaxElementHöhe,
  isTor,
  mauer,
  toraufhängung,
  torschloss,
  aufbautyp,
  anschlagprofilAuswahlwert,
  anschlagprofilfarbeAuswahlwert,
}: PfostenMetaParameter): PfostenMetaParameter {
  if (isTor && mauer && !toraufhängung && !torschloss) {
    throw new Error(
      "für ein Tor an Mauer muss toraufhängung oder torschloss in pfostenMetaParameter definiert sein"
    );
  }
  // Torpfosten haben eigene Pfostenart, sollen aber trotzdem den Nicht-Torpfostenarten zugeordnet werden
  // Desshalb der Weg über die PfostenartRelation
  pfostenartAuswahlwert = isTor
    ? findPathIntersectionCached("pfostenFunctions.js:198")(
        pfostenartAuswahlwert,
        (p: PAuswahlwert) => p?.Objekt?.PfostenartRelation?.Torpfostenart?.Auswahlwert
      )
    : pfostenartAuswahlwert;

  pfostenartAuswahlwert =
    (findPathIntersectionCached("pfostenFunctions.js:199")(
      pfostenMetas,
      (p: PPfosten) => p.Pfostentyp.Pfosten.Pfostenart.Auswahlwert,
      following_are_optionals,
      pfostenartAuswahlwert,
      (p: PAuswahlwert) => p,
      pfosten?.arrangement?.pfostenartAuswahlwert,
      (p: PAuswahlwert) => p
    ) as MAuswahlwert) ?? pfostenartAuswahlwert;

  // das muss ausgelagert werden. Entscheidung hier, aber Code bzw. Weiche woanders
  const pathes = isTor
    ? _.flatten(
        elemente
          .filter((e) => !Commons.isEmpty((e as any)?.Mass.Tor))
          .map((e) => [e, (e: any) => e.Pfostenfarbe.Auswahlwert])
      )
    : [elemente, (e) => e.Pfostenfarbe.Auswahlwert];

  const möglichePfostenfarbenSortedByVoreinstellung = _.sortBy(
    filterPathIntersectionCached("pfostenFunctions:439")(
      meta as MPfosten,
      (p: PPfosten) => p.Pfostentyp.Pfosten.Pfostenfarbe.Auswahlwert
    ) as MAuswahlwert[],
    (p) => (p.Voreinstellung ? 0 : 1)
  );

  pfostenfarbeAuswahlwert =
    findPathIntersection(
      möglichePfostenfarbenSortedByVoreinstellung,
      (p: PAuswahlwert) => p,
      following_are_optionals,
      // @ts-ignore
      ...pathes,
      pfostenfarbeAuswahlwert,
      (p: PAuswahlwert) => p
    ) ?? pfostenfarbeAuswahlwert;

  // das sollte dann wahrscheinlich auch ausgelagert werden.
  bodenbefestigungAuswahlwert = findPathIntersectionCached("pfostenFunctions.js:219")(
    // Hier müssen beide Pfostenarten berücksichtigt werden, da trotz gewählter Steckpfosten
    // auch Klemmpfosten in der Szene vorkommen können (Tore)
    pfostenartAuswahlwert,
    (p: PAuswahlwert) => p.Objekt.Pfosten.Bodenbefestigung.Auswahlwert,
    following_are_optionals,
    bodenbefestigungAuswahlwert,
    (p: PAuswahlwert) => p
  ) as MAuswahlwert;

  elementhalterAuswahlwert = findPathIntersectionCached("pfostenFunctions.js:229")(
    pfostenartAuswahlwert,
    (p: PAuswahlwert) => p.Objekt.Pfosten.Elementhalter.Auswahlwert,
    following_are_optionals,
    elementhalterAuswahlwert,
    (p: PAuswahlwert) => p
  ) as MAuswahlwert;

  pfostenkappeAuswahlwert = findPathIntersectionCached("pfostenFunctions.js:252")(
    pfostenartAuswahlwert,
    (p: PAuswahlwert) => p.Objekt.Pfosten.Pfostenkappe.Auswahlwert,
    following_are_optionals,
    pfostenkappeAuswahlwert,
    (p: PAuswahlwert) => p
  ) as MAuswahlwert;

  const torpfostenseite = torschloss ? "Schloss" : toraufhängung ? "Aufhängung" : null;

  const matchesSerie = (p) => APP.arrangement.serie.Pfostentyp?.includes(p.Pfostentyp);
  const scoreWinkelbereiche = (p) => findMatchingRotation(p.WinkelbereicheParsed, angles)?.score;
  const matchesPfostenfarbe = (p) => p.Pfostenfarbe[0].Auswahlwert == pfostenfarbeAuswahlwert;
  const pfostenHöhe = (p) => p.Pfostenmass.Standardhöhe;
  const toleranz = 0.001;
  const matchesHöhe = (p) => pfostenHöhe(p) + toleranz > gesuchtePfostenHöhe;
  const matchesLänge = (p) =>
    pfostenHöhe(p) + toleranz > gesuchtePfostenHöhe + p.Pfostenmass.PositionY;
  const matchesTor = (p) =>
    isTor == p.Pfostenmass.Tor && (!isTor || p.Torpfostenseite == torpfostenseite);
  const matchesAusgleichGefälle = (p) => ausgleichGefälle == p.GefälleAusgleich;
  const matchesSteckpfosten = (p) =>
    pfostenartAuswahlwert == null && (p.Pfostenart?.some((b) => b.Key == "Steckpfosten") ?? false);
  const matchesPfostenart = (p) =>
    pfostenartAuswahlwert == null ||
    (p.Pfostenart?.some((b) => b.Auswahlwert == pfostenartAuswahlwert) ?? false);
  const matchesBodenbefestigung = (p) =>
    bodenbefestigungAuswahlwert == null ||
    (p.Bodenbefestigung?.some((b) => b.Auswahlwert == bodenbefestigungAuswahlwert) ?? false);
  const matchesElementhalter = (p) =>
    isTor ||
    elementhalterAuswahlwert == null ||
    (p.Elementhalter?.some((b) => b.Auswahlwert == elementhalterAuswahlwert) ?? false);

  const isElementhalter = elemente.some(
    (e) => (e as ZElement)?.isElementhalter || isElementhalterFn(e as MElement)
  );

  if (mauer) {
    gesuchtePfostenHöhe += APP.settings.delta_umprofil_höhenversatz;
    const meta = APP.richMetadata.getUMProfilMeta({
      aufbautyp,
      pfostenfarbe: pfostenfarbeAuswahlwert?.Objekt[0],
      pfostentyp: APP.richMetadata.findPfostentypForPfostenartAuswahl(pfostenartAuswahlwert),
      pfostenartAuswahlwert: APP.arrangement.pfostenartAuswahlwert,
      mauer,
      gesuchtePfostenHöhe,
      toraufhängung,
      torschloss,
      anschlagprofilAuswahlwert,
      anschlagprofilfarbeAuswahlwert,
      isElementhalter,
      isEckadapter: false,
      isWinkelbereichU: false,
    });
    if (meta) {
      const mmr = getMMR(meta);
      const { box, translation, echteLänge } = pfosten
        ? createAndPositionUMProfilBox(pfosten, mmr, gesuchtePfostenHöhe, meta)
        : { box: null, translation: v3(), echteLänge: 0 };
      return {
        pfosten,
        meta,
        isUMProfil: true,
        hasAnker: false,
        pfostenfarbeAuswahlwert,
        anschlagprofilfarbeAuswahlwert,
        pfostenartAuswahlwert,
        isAufgeschraubt: false,
        montageadapterAuswahlwert,
        verbundankerAuswahlwert,
        ummantelungAuswahlwert,
        ankerAuswahlwert: ankerAuswahlwert,
        pfostenkappeAuswahlwert,
        warning: null,
        box,
        breite: meta?.Standard?.x ?? 0,
        echteLänge,
        dy: translation.y,
        gesuchtePfostenHöhe,
        angles,
      };
    }
  }

  const nanToNull = (a: number) => (Number.isNaN(a) ? null : a);

  const pfostenScore = (p) =>
    1 +
    (matchesSerie(p) ? 0 : -10000) +
    (nanToNull(scoreWinkelbereiche(p) * 0.001) ?? -10000) +
    (matchesTor(p) ? 0 : -10000) +
    (matchesPfostenart(p) ? 0 : -1100) +
    // Steckpfosten führen eher zu Verkratzungen, deswegen u.A. Klemmpfosten bevorzugen
    (matchesSteckpfosten(p) ? -0.01 : 0) +
    (matchesBodenbefestigung(p) ? 0 : -100) +
    (matchesElementhalter(p) ? 0 : -100) +
    (matchesPfostenfarbe(p) ? 0 : -50) +
    (matchesLänge(p) ? 0 : -50) +
    (matchesAusgleichGefälle(p) ? 0 : -100) +
    (matchesHöhe(p)
      ? // Je höher der Pfosten zu hoch ist desto schlechter
        gesuchtePfostenHöhe -
        pfostenHöhe(p) +
        // Bei anliegenden MassElementen Fertigung bevorzugen, ansonsten kriegen
        // kürzbare Pfosten 2 cm Nachteil, so dass bei ungefähr richtiger Länge Fix bevorzugt wird
        (p.Pfostenmass.Variabel == "fertigung"
          ? 0
          : !isMaxElementHöhe
            ? p.Pfostenmass.Variabel == "fix"
              ? -0.01
              : -0.02
            : 0)
      : -1000 +
        // Je weniger der Pfosten passt desto schlechter
        (pfostenHöhe(p) -
          gesuchtePfostenHöhe +
          // Bei anliegenden MassElementen Fertigung bevorzugen, ansonsten kriegen
          // kürzbare Pfosten 2 mm Nachteil, so dass bei ungefähr richtiger Länge Fix bevorzugt wird
          (p.Pfostenmass.Variabel == "fertigung"
            ? 0
            : !isMaxElementHöhe
              ? p.Pfostenmass.Variabel == "fix"
                ? -0.001
                : -0.002
              : 0)));

  const sorted = pfostenMetas
    .map((p) => ({
      meta: p,
      score: pfostenScore(p),
      Key: p.Key,
      mSerie: matchesSerie(p),
      mWinkelbereiche: scoreWinkelbereiche(p),
      mTor: matchesTor(p),
      mPfostenart: matchesPfostenart(p),
      mBodenbefestigung: matchesBodenbefestigung(p),
      mElementhalter: matchesElementhalter(p),
      mPfostenfarbe: matchesPfostenfarbe(p),
      mHöhe: matchesHöhe(p),
      mLänge: matchesLänge(p),
      mAusgleichGefälle: matchesAusgleichGefälle(p),
      Variabel: p.Pfostenmass.Variabel,
      höhe: pfostenHöhe(p),
      höheSoll: gesuchtePfostenHöhe,
      PositionY: p.Pfostenmass.PositionY,
      Bodenbefestigung: p.Bodenbefestigung.map((b) => b.Key.substringBefore(" ")).join(","),
      Eckpfosten: p.Eckpfosten,
    }))
    .sort((a, b) => b.score - a.score);
  meta = _.first(sorted)?.meta ?? APP.richMetadata.getDummyPfosten();
  // console.log({ sorted, meta });

  const pfostenWarning = (p) =>
    !scoreWinkelbereiche(p)
      ? "kein passender Eckpfosten"
      : !matchesTor(p)
        ? "kein passender Torpfosten"
        : !matchesLänge(p)
          ? "kein von der Länge passender Pfosten"
          : !matchesHöhe(p)
            ? "kein von der Höhe passender Pfosten"
            : null;

  const warning = pfostenWarning(meta);
  const is_klemmpfosten = isKlemmpfosten(pfostenartAuswahlwert);
  const is_aufgeschraubt = isAufgeschraubt(bodenbefestigungAuswahlwert);
  const hasVerbundanker = meta.Verbundanker;
  const hasUmmantelung = meta.Ummantelung;
  const hasMontageadapter = meta.Montageadapter;
  const hasAnker =
    ankerAuswahlwert &&
    (!Commons.isEmpty(meta.Sturmanker) || !Commons.isEmpty(meta.Windanker)) &&
    Commons.isEmpty(meta.Torpfostenseite);
  const positionY = meta.Pfostenmass.PositionY;
  const box = getBoxUnclipped(meta);

  const sollLänge =
    (meta.Pfostenmass.Variabel == "fix" ? meta.Pfostenmass.Standardhöhe : gesuchtePfostenHöhe) -
    positionY;
  const echteLänge = Math.min(box.getSize(v3()).y, sollLänge);

  box.min.y = box.max.y - echteLänge;
  const bodenplatteY = is_aufgeschraubt ? 0.01 : 0;
  const dy = positionY - box.min.y + sollLänge - echteLänge + bodenplatteY;
  box.translate(v3(0, dy, 0));

  return {
    pfosten,
    meta,
    isUMProfil: false,
    hasAnker,
    pfostenfarbeAuswahlwert,
    pfostenartAuswahlwert,
    isAufgeschraubt: is_aufgeschraubt,
    montageadapterAuswahlwert: hasMontageadapter
      ? montageadapterAuswahlwert ??
        (is_aufgeschraubt && APP.richMetadata.getAuswahlWertVoreinstellungForKey("Montageadapter"))
      : null,
    verbundankerAuswahlwert: hasVerbundanker
      ? verbundankerAuswahlwert ??
        (is_aufgeschraubt && APP.richMetadata.getAuswahlWertVoreinstellungForKey("Verbundanker"))
      : null,
    ummantelungAuswahlwert: hasUmmantelung
      ? ummantelungAuswahlwert ??
        (is_klemmpfosten && APP.richMetadata.getAuswahlWertVoreinstellungForKey("Ummantelung"))
      : null,
    bodenbefestigungAuswahlwert,
    elementhalterAuswahlwert,
    ankerAuswahlwert,
    pfostenkappeAuswahlwert,
    warning,
    box,
    breite: meta?.Pfostenmass?.Breite ?? 0,
    echteLänge,
    dy,
    gesuchtePfostenHöhe,
    angles,
  };
}

export function getBoxUnclipped(meta: any): Box3 {
  return APP.modelsHelper.getBoundingBox(getMMR(meta))?.clone() as Box3;
}

export function isAufgeschraubt(bodenbefestigungAuswahlwert?: MAuswahlwert): boolean {
  return bodenbefestigungAuswahlwert?.Key === "Aufgeschraubt auf festem Boden";
}

export function isKlemmpfosten(pfostenartAuswahlwert?: MAuswahlwert): boolean {
  return pfostenartAuswahlwert?.Key === "Klemmpfosten";
}

export function isWinkelbereichUForPfosten(pfosten: Pfosten, angle: number) {
  return getAngleIntervalType(pfosten, angle) === "U";
}

export function getMPfostenBreite(pfostenMeta: MPfosten | MUMProfil) {
  return (
    (pfostenMeta as MPfosten)?.Pfostenmass?.Breite ??
    (pfostenMeta as MUMProfil)?.Standard?.x ??
    pfostenbreiteCirca
  );
}

export function getEckadapterInfo(pfostenMeta: MPfosten) {
  const pfostenBreite = getMPfostenBreite(pfostenMeta);
  const pfostenRadius = pfostenBreite / 2;
  const eckadapterMeta = pfostenMeta.Eckadapter;
  if (eckadapterMeta == null) {
    throw new Error("getEckadapterInfo: pfostenMeta.Eckadapter is null for " + pfostenMeta?.Key);
  }
  const pfostenEcke = (Math.sqrt(2) / 2) * pfostenBreite;
  const adapterMitte = pfostenEcke + eckadapterMeta.PositionX;
  const adapterRand = adapterMitte + eckadapterMeta.Breite / 2;
  // eckadapterDelta ist die Entfernung vom Innenkreis des Pfostens
  // (Kreis mit Durchmesser der Pfostenbreite) zur Adaptermitte.
  // Erklärung:
  // der quadratische Pfosten mit Eckadapter skizziert:
  // +---+
  // | x |
  // +---e
  //      a
  // - x ist die Pfostenmitte
  // - e ist die Ecke des Pfostens (pfostenEcke)
  // - a ist der flache Rand des Adapters, an dem das UMProfil anliegt
  // Die Positionierung der Elemente entlang eines Segmentes bezieht sich auf die
  // Pfostenmitte und den Radius des Pfostens. Mit dem zusätzlichen Summanden
  // eckadapterDelta ergibt sich die exakte Position des Eckadaptersrands.
  // eckadapterDelta ist immer positiv.
  // Wenn kein Eckadapter vorhanden ist, ist eckadapterDelta 0 im weiteren Code der Anwendung.
  const eckadapterDelta = adapterRand - pfostenRadius;
  const eckadapterGap = 0.0003;
  return { eckadapterMeta, eckadapterDelta, adapterMitte, eckadapterGap };
}

export function getVorläufigeElementeAnEcke(ecke: Ecke): ZElement[] {
  return ecke.segmente.map((segment) => {
    const eckeIndex = segment.ecken[0] === ecke ? 0 : 1;
    const elemente = segment.getElemente();
    const element = elemente.at(eckeIndex === 0 ? 0 : -1);
    if (element) {
      const x = segment.getX(element, eckeIndex);
      const xEnd = eckeIndex * segment.breite;
      const epsilon = 0.001;
      const elementAmPfosten = Math.abs(x - xEnd) < pfostenbreiteCirca + epsilon;
      if (elementAmPfosten) {
        return element;
      }
    }
    return null;
  });
}

export function getLückeZwischen(segment: Segment, teilLinks: any, teilRechts: any) {
  const a = segment.getX(teilLinks, 1) + teilLinks.gapRechts;
  const b = segment.getX(teilRechts, 0) - teilRechts.gapLinks;
  const epsilon = 0.005;
  const abstand = b - a;
  const istLücke = Math.abs(abstand) > epsilon;
  return { a, b, abstand, istLücke };
}

export function getLückenTeile(element: ZElement, pfosten: Pfosten) {
  const umProfil =
    element.pfostenLinks === pfosten ? element.umprofilLinks : element.umprofilRechts;
  const umProfilOderPfosten = umProfil ?? pfosten;
  const [teilLinks, teilRechts] =
    element.pfostenLinks === pfosten
      ? [umProfilOderPfosten, element]
      : [element, umProfilOderPfosten];
  return [teilLinks, teilRechts];
}

export function isLongAnschlag(meta: MKeyTable) {
  return meta?.Key == "611_Anschlagprofil 180 cm Tor an Mauer";
}

export function getUMProfilModel(
  pfostenOrUMProfil: Pfosten | UMProfil,
  gesuchtePfostenHöhe: number,
  umpfrofilMeta: MUMProfil,
  öffnungNachLinks: boolean
) {
  const mmr = getMMR(umpfrofilMeta);
  const { box, translation, echteLänge } = createAndPositionUMProfilBox(
    pfostenOrUMProfil,
    mmr,
    gesuchtePfostenHöhe,
    umpfrofilMeta
  );
  const model = createAndPositionUMProfilModel(
    pfostenOrUMProfil,
    mmr,
    umpfrofilMeta,
    öffnungNachLinks,
    echteLänge,
    translation
  );
  return { model, box };
}

export function createAndPositionUMProfilBox(
  pfostenOrUMProfil: Pfosten | UMProfil,
  mmr: MModelMaterialRelation,
  gesuchtePfostenHöhe: number,
  umpfrofilMeta: MUMProfil
) {
  const sollLänge = gesuchtePfostenHöhe - umpfrofilMeta.PositionY;
  const box = APP.modelsHelper.getBoundingBox(mmr)?.clone();
  const echteLänge = Math.min(
    box.getSize(v3()).y,
    isLongAnschlag(pfostenOrUMProfil.meta) ? Infinity : sollLänge
  );
  // Box auf die korrekte Länge zuschneiden
  box.min.y = box.max.y - echteLänge;
  // Translationsvektor berechnen, der für Box UND Model gilt
  const center = box.getCenter(v3());
  const dy = umpfrofilMeta.PositionY - box.min.y + sollLänge - echteLänge;
  const translation = v3(-center.x, dy, -center.z);
  // Box verschieben
  box.translate(translation);
  // Achtung, die BoundingBox kann von der Standard-Size aus der UMProfil-Tabelle abweichen. Letztere hat Prio!
  box.setFromCenterAndSize(box.getCenter(v3()), box.getSize(v3()).setX(umpfrofilMeta.Standard.x));
  return { box, translation, echteLänge };
}

export function createAndPositionUMProfilModel(
  pfostenOrUMProfil: Pfosten | UMProfil,
  mmr: MModelMaterialRelation,
  umpfrofilMeta: MUMProfil,
  öffnungNachLinks: boolean,
  echteLänge: number,
  translation: Vector3
) {
  const model = pfostenOrUMProfil.arrangement.ghostPool.getForModelMaterialRelation(
    pfostenOrUMProfil,
    mmr,
    {
      size: v3(Number.POSITIVE_INFINITY, echteLänge, Number.POSITIVE_INFINITY),
      fixGridPosition: v3(0.5, 1, 0.5),
      gehrung45: false,
    }
  );
  model.position.copy(translation);
  if (/U-.*/.test(umpfrofilMeta.Typ)) {
    model.quaternion.copy(öffnungNachLinks ? Maths3.quaternion.y90 : Maths3.quaternion.y270);
  } else {
    model.quaternion.copy(öffnungNachLinks ? Maths3.quaternion.zero : Maths3.quaternion.y180);
  }
  model.update3d();
  return model;
}

export function initInsertPfostenFunction(segment: Segment) {
  // die genau Art der Pfosten wird erst im update3d festgelegt,
  // hiermit wird erst mal festgelegt, wo überhaupt Pfosten stehen
  const pfostenBreiteEpsilon = pfostenbreiteCirca;
  const pfostenAbstandElement = pfostenBreiteEpsilon / 2;
  segment.pfosten = [];
  const elementPfostenMap = new Map();
  const usedPfosten = new Set();
  segment.getElemente().forEach((element) => {
    const { pfostenLinks, pfostenRechts } = element;
    elementPfostenMap.set(element, { pfostenLinks, pfostenRechts });
    element.pfostenLinks = null;
    element.pfostenRechts = null;
    [pfostenLinks, pfostenRechts].forEach((pfosten) => {
      if (pfosten && !pfosten?.isPfostenAnEcke) {
        pfosten.elemente = [];
      }
    });
  });
  const insertPfosten = (partLinks, partRechts) => {
    if (!(partLinks?.isElement || partRechts?.isElement)) {
      return;
    }

    // if (APP.debugit && partLinks?.id == 55 && partRechts?.id == 338) debugger;
    // vorläufige, nur ungefähre Positionierung für die Sortierung
    const x =
      partLinks && partRechts
        ? (segment.getX(partLinks, 1) + segment.getX(partRechts, 0)) / 2
        : partLinks
          ? segment.getX(partLinks, 1) + pfostenAbstandElement
          : segment.getX(partRechts, 0) - pfostenAbstandElement;
    const usePfostenOnce = (pfosten) => {
      if (usedPfosten.has(pfosten)) {
        return null;
      }
      usedPfosten.add(pfosten);
      return pfosten;
    };
    let pfosten = null;
    let anMauer = false;
    const anfangDelta = Math.abs(x);
    const endeDelta = Math.abs(x - segment.breite);
    // const eckDelta = Math.min(anfangDelta, endeDelta);
    const segmentAnfang = anfangDelta < pfostenBreiteEpsilon;
    const segmentEnde = endeDelta < pfostenBreiteEpsilon;
    // const eckpfostenTreshold = 0.08;
    const behandleSegmentGrenze = (ecke: Ecke, pfostenZurPotentiellenVerwendung?: Pfosten) => {
      anMauer = ecke.anMauer;
      if (!anMauer) {
        const potentiellNeuerPfosten = usePfostenOnce(
          ecke.ensurePfosten(pfostenZurPotentiellenVerwendung)
        );
        pfosten = potentiellNeuerPfosten;
        if (potentiellNeuerPfosten !== pfostenZurPotentiellenVerwendung) {
          ecke.gelöschterPfosten = pfostenZurPotentiellenVerwendung;
        }
      }
    };
    if (APP.loggit && segment.segmentIndex == 2) {
      console.log({ segmentAnfang, segmentEnde });
    }
    if (segmentAnfang) {
      behandleSegmentGrenze(
        segment.ecken[0],
        segment.ecken[0].pfosten ?? elementPfostenMap.get(partRechts)?.pfostenLinks
      );
    } else if (segmentEnde) {
      behandleSegmentGrenze(
        segment.ecken[1],
        segment.ecken[1].pfosten ?? elementPfostenMap.get(partLinks)?.pfostenRechts
      );
    }
    if (pfosten == null) {
      pfosten =
        usePfostenOnce(elementPfostenMap.get(partLinks)?.pfostenRechts) ??
        usePfostenOnce(elementPfostenMap.get(partRechts)?.pfostenLinks);
      if (pfosten?.isPfostenAnEcke) {
        // ein Eckpfosten, der aber nicht segmentAnfang oder -Ende ist
        // wenn ein Element eines anderen Segmentes über Eck anliegt, muss der Eckpfosten genau auf der Ecke stehen
        if (pfosten.isPfostenAnEckeUnverrückbar(segment)) {
          // wir brauchen einen neuen Pfosten, weil das Element zu weit vom Eckpfosten entfernt steht
          usedPfosten.delete(pfosten);
          pfosten = null;
        } else {
          // der Eckpfosten wird vom weiteren Code potentiell raus aus der Ecke und ans Element verschoben
        }
      }
      pfosten = pfosten ?? new Pfosten(segment);
      usedPfosten.add(pfosten);
      // if (partLinks && partRechts && pfosten.isPfostenAnEcke) debugger;
      pfosten.position.x = x;
    }

    if (pfosten.ecke == null) {
      pfosten.position.z = 0;
    }

    if (partLinks && partRechts && pfosten.ecke) {
      pfosten.ecke.pfosten = null;
      pfosten.ecke = null;
      pfosten.parent = segment;
      pfosten.position.set(x, 0, 0);
    }
    pfosten.parent = pfosten.isPfostenAnEcke ? segment.arrangement : segment;
    pfosten.anMauer = anMauer;
    pfosten.isGrenzpfosten =
      pfosten?.isPfostenAnEcke || (!anMauer && (segmentAnfang || segmentEnde));
    const addElementToPfosten = (element, pfosten) => {
      if (!pfosten.elemente.includes(element)) {
        pfosten.elemente.push(element);
      }
    };
    if (partLinks) {
      partLinks.pfostenRechts = pfosten;
      addElementToPfosten(partLinks, pfosten);
    }
    if (partRechts) {
      partRechts.pfostenLinks = pfosten;
      addElementToPfosten(partRechts, pfosten);
    }
    pfosten && segment.pfosten.push(pfosten);
    return pfosten;
  };
  return insertPfosten;
}

export function updateTeilModel(
  owner: Jaco,
  condition: boolean,
  propertyKey: string,
  mmrKey: string,
  positioningFn: (model: ModelGhost) => void,
  size?: Vector3 | [number, number, number]
): void {
  if (condition) {
    const mmr = APP.richMetadata.findModelMaterialRelationForKey(mmrKey);
    if (!mmr) {
      console.warn(`ModelMaterialRelation "${mmrKey}" fehlt!`);
      debugger;
    }
    const agt = owner.arrangement as Arrangement;
    const model = agt.ghostPool.getForModelMaterialRelation(owner, mmr, {
      size: v3(
        ...(size ?? [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY])
      ),
      fixGridPosition: v3(0, 0, 0),
    });
    owner[propertyKey] = model;
    if (model) {
      positioningFn(model);
      model.update3d();
    }
  } else {
    owner[propertyKey] = null;
  }
}
